import { DependencyList, RefObject, useEffect, useRef, useState } from "react"

// a11y: report whether a table overflows its scroll container, so the container is made a focusable,
// keyboard-scrollable group only when there is actually something to scroll — a normal-width table
// stays a plain wrapper with no tab stop. Overflow is measured from rendered rects (Safari
// under-reports scrollWidth, the bug the table arrow-key handler works around) and re-checked on
// resize. Shared by ready, workkeys, and qti-components (qti-pwa keeps its own copy — separate
// repo). Pass `deps` (e.g. the table content/children) to re-measure when the rendered table
// changes. SC 2.1.1.
export const useA11yTableOverflow = (
  deps: DependencyList = []
): { ref: RefObject<HTMLDivElement>; overflows: boolean } => {
  const ref = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const box = ref.current
    const table = box?.querySelector("table")
    // return undefined (not a bare return) to satisfy stricter noImplicitReturns configs.
    if (!box || !table) return undefined

    const measure = () => {
      const maxScrollLeft =
        box.scrollLeft +
        Math.ceil(table.getBoundingClientRect().right - box.getBoundingClientRect().right)
      setOverflows(maxScrollLeft > 0)
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(box)
    observer.observe(table)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { ref, overflows }
}
